import qs from "qs";
import { emptyApi } from "./emptyApi";
import { DOWNLOAD_FORMAT_TYPES } from "constants/bookkeeping";

export type CustomReportCategory = {
  uuid: string;
  name: string;
  identifier: number;
};

export type ReportCategoryData = {
  uuid: string;
  name: string;
  identifier: number;
  summary_amount: number;
  formatted_amount: string;
  category_type: string;
  category_amount: number;
  percentage_value: number;
  formatted_category_amount: string;
};

export type ReportGroup = {
  uuid: string;
  title: string;
  report_categories: ReportCategoryData[];
  group_amount: number;
  group_percentage: number;
};

export type ReportPeriod = {
  start_date: string;
  end_date: string;
  groups: ReportGroup[];
};

export type ChartConfig = {
  chart_type: ChartType;
  x_axis: "PERIOD";
  y_axis: AnalysisModeType;
  series: {
    mode: SeriesModeType;
    group_id: string | null;
  };
};

export type CustomReport = {
  uuid: string;
  title: string;
  description?: string;
  created_at: string;
  created_by: string;
  is_template: boolean;
  percentage_mode: string;
  percentage_mode_label: string;
  categories: CustomReportCategory[];
  category_groups: CustomReportGroup[];
  periods?: Record<string, ReportPeriod>;
  comparison_report_type?: string;
  start_date?: string | null;
  end_date?: string | null;
  accounting_method?: "cash" | "accrual";
  total?: number;
  formatted_total?: string;
  document_id?: string;
  excel_document_id?: string;
  groups: PreviewGroup[];
  chart_config: ChartConfig;
};

type CustomReportBaseProps = {
  entityId: string;
};

export type CustomReportFilterParams = {
  startDate?: string | null;
  endDate?: string | null;
  accountingMethod?: string;
  comparisonReportType?: string | null;
  showNonZeroAccounts?: boolean;
  action?: DOWNLOAD_FORMAT_TYPES;
  export_to_mail?: boolean;
  folderId?: string;
  percentageMode?: string | null;
};

export const CHART_TYPE = {
  BAR: "BAR",
} as const;

export const ANALYSIS_MODE = {
  AMOUNT: "AMOUNT",
  PERCENT: "PERCENT",
} as const;

export const SERIES_MODE = {
  GROUPS: "GROUPS",
  CATEGORIES: "CATEGORIES",
} as const;

export const CUSTOM_REPORT_MODE = {
  EDIT: "edit",
  TEMPLATE: "template",
} as const;

export type ChartType = keyof typeof CHART_TYPE;
export type AnalysisModeType = keyof typeof ANALYSIS_MODE;
export type SeriesModeType = keyof typeof SERIES_MODE;

export type ChartSeriesItem = {
  uuid: string;
  name: string;
  data: number[];
};

export type CustomReportChartResponse = {
  chart_type: string;
  series_type: SeriesModeType;
  analysis_mode: AnalysisModeType;
  x_axis: {
    label: string;
    data: string[];
  };
  y_axis: {
    label: string;
  };
  series: ChartSeriesItem[];
};

type CustomReportChartParams = {
  entityId: string;
  reportId: string;
  startDate?: string | null;
  endDate?: string | null;
  accountingMethod?: string;
  comparisonReportType?: string | null;
  showNonZeroAccounts?: boolean;
  chartType: ChartType;
  analysisMode: AnalysisModeType;
  seriesGroupId?: string | null;
  seriesMode?: string;
};

type CreateCustomReportPayload =
  | { title: string; template_id?: never }
  | { template_id: string; title?: never };

type CreateCustomReportProps = CustomReportBaseProps & {
  payload: CreateCustomReportPayload;
};

type UpdateCustomReportProps = CustomReportBaseProps & {
  reportId: string;
  payload: {
    title?: string;
    start_date?: string | null;
    end_date?: string | null;
    accounting_method?: string | null;
    comparison_report_type?: string | null;
    percentage_mode?: string | null;
    groups?: PreviewGroup[];
    chart_config?: ChartConfig;
    action?: DOWNLOAD_FORMAT_TYPES;
    export_to_mail?: boolean;
    folder_id?: string;
  };
};

type GroupBaseProps = {
  entityId: string;
  reportId: string;
};

type CreateGroupProps = GroupBaseProps & {
  payload: {
    title: string;
    category_ids: string[];
  };
};

type UpdateGroupProps = GroupBaseProps & {
  groupId: string;
  payload: {
    title: string;
    category_ids: string[];
  };
};

type DeleteGroupProps = GroupBaseProps & {
  groupId: string;
};

export type CustomReportGroup = {
  uuid: string;
  title: string;
  categories: CustomReportCategory[];
};

export type PreviewGroup = {
  uuid?: string;
  title: string;
  category_ids: string[];
};

export type PreviewReportPayload = {
  entityId: string;
  reportId: string;
  start_date: string | null;
  end_date: string | null;
  show_non_zero_accounts: boolean;
  accounting_method: string;
  comparison_report_type: string | null;
  percentage_mode: string | null;
  groups: PreviewGroup[];
};

export type PreviewChartPayload = PreviewReportPayload & {
  chart_config: ChartConfig;
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getCustomReports: build.query<CustomReport[], CustomReportBaseProps>({
      query: ({ entityId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/`,
      }),
      providesTags: (result) => (result ? ["CUSTOM_REPORTS"] : []),
    }),

    getCustomReportById: build.query<
      CustomReport,
      CustomReportBaseProps & { reportId: string } & CustomReportFilterParams
    >({
      query: ({
        entityId,
        reportId,
        startDate,
        endDate,
        accountingMethod,
        comparisonReportType,
        showNonZeroAccounts,
        action,
        export_to_mail,
        folderId,
        percentageMode,
      }) => {
        const queryString = qs.stringify(
          {
            start_date: startDate,
            end_date: endDate,
            accounting_method: accountingMethod,
            comparison_report_type: comparisonReportType,
            show_non_zero_accounts: showNonZeroAccounts,
            action,
            export_to_mail,
            folder_id: folderId,
            percentage_mode: percentageMode,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/${queryString}`,
        };
      },
      providesTags: (result) => (result ? ["CUSTOM_REPORTS"] : []),
    }),

    createCustomReport: build.mutation<CustomReport, CreateCustomReportProps>({
      query: ({ entityId, payload }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CUSTOM_REPORTS"],
    }),

    updateCustomReport: build.mutation<CustomReport, UpdateCustomReportProps>({
      query: ({ entityId, reportId, payload }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["CUSTOM_REPORTS"],
    }),

    deleteCustomReport: build.mutation<
      { success: boolean },
      CustomReportBaseProps & { reportId: string }
    >({
      query: ({ entityId, reportId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["CUSTOM_REPORTS"],
    }),

    createReportGroup: build.mutation<CustomReportGroup, CreateGroupProps>({
      query: ({ entityId, reportId, payload }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/groups/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CUSTOM_REPORTS"],
    }),

    updateReportGroup: build.mutation<CustomReportGroup, UpdateGroupProps>({
      query: ({ entityId, reportId, groupId, payload }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/groups/${groupId}/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["CUSTOM_REPORTS"],
    }),

    deleteReportGroup: build.mutation<{ success: boolean }, DeleteGroupProps>({
      query: ({ entityId, reportId, groupId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/groups/${groupId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["CUSTOM_REPORTS"],
    }),

    getCustomReportChart: build.query<
      CustomReportChartResponse,
      CustomReportChartParams
    >({
      query: ({
        entityId,
        reportId,
        startDate,
        endDate,
        accountingMethod,
        comparisonReportType,
        showNonZeroAccounts,
        chartType,
        analysisMode,
        seriesGroupId,
        seriesMode,
      }) => {
        const queryString = qs.stringify(
          {
            start_date: startDate,
            end_date: endDate,
            accounting_method: accountingMethod,
            comparison_report_type: comparisonReportType,
            show_non_zero_accounts: showNonZeroAccounts,
            chart_type: chartType,
            y_axis: analysisMode,
            series_group_id: seriesGroupId,
            series_mode: seriesMode,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/chart/${queryString}`,
        };
      },
      providesTags: (result) => (result ? ["CUSTOM_REPORTS"] : []),
    }),

    previewCustomReport: build.mutation<CustomReport, PreviewReportPayload>({
      query: ({ entityId, reportId, ...body }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/preview/`,
        method: "POST",
        body,
      }),
    }),

    previewCustomReportChart: build.mutation<
      CustomReportChartResponse,
      PreviewChartPayload
    >({
      query: ({ entityId, reportId, ...body }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/chart/`,
        method: "POST",
        body,
      }),
    }),

    exportCustomReportChart: build.mutation<
      { document_id: string },
      {
        entityId: string;
        reportId: string;
        graphs: string[];
        export_to_mail?: boolean;
        folder_id?: string;
        start_date?: string | null;
        end_date?: string | null;
      }
    >({
      query: ({
        entityId,
        reportId,
        graphs,
        export_to_mail,
        folder_id,
        start_date,
        end_date,
      }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/custom_reports/${reportId}/export_graphs/`,
        method: "POST",
        body: { graphs, export_to_mail, folder_id, start_date, end_date },
      }),
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetCustomReportsQuery,
  useGetCustomReportByIdQuery,
  useLazyGetCustomReportByIdQuery,
  useCreateCustomReportMutation,
  useUpdateCustomReportMutation,
  useDeleteCustomReportMutation,
  useCreateReportGroupMutation,
  useUpdateReportGroupMutation,
  useDeleteReportGroupMutation,
  useGetCustomReportChartQuery,
  usePreviewCustomReportMutation,
  usePreviewCustomReportChartMutation,
  useExportCustomReportChartMutation,
} = extendedApi;
